import { useEffect, useMemo, useState, useCallback } from 'react';
import { listClassrooms, archiveClassroom, deleteClassroom } from '../api/classroomApi';
import { appToast } from '../../../../components/AppToast';
import { appConfirm } from '../../../../components/AppConfirmModal';

const DEFAULT_PAGE_SIZE = 10;

export function useClassroomListScreen() {
  const [viewMode, setViewMode] = useState('active'); // 'active' | 'archived' | 'all'
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({
    totalCount: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 1,
  });

  const queryParams = useMemo(() => {
    return {
      page,
      pageSize,
      viewMode,
      searchText: searchText.trim(),
    };
  }, [page, pageSize, viewMode, searchText]);

  const loadClassrooms = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await listClassrooms(queryParams);
      setItems(response.items || []);
      setMeta({
        totalCount: response.total_count ?? 0,
        page: response.page ?? 1,
        pageSize: response.page_size ?? pageSize,
        totalPages: response.total_pages ?? 1,
      });
    } catch (requestError) {
      const msg = requestError?.response?.data?.detail || 'Failed to load classrooms.';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setIsLoading(false);
    }
  }, [queryParams, pageSize]);

  useEffect(() => {
    loadClassrooms();
  }, [loadClassrooms]);

  const handleArchive = async (id) => {
    try {
      const res = await archiveClassroom(id);
      appToast.success(res?.message || 'Classroom status updated successfully.');
      loadClassrooms();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to update classroom status.';
      appToast.error(typeof msg === 'string' ? msg : 'Failed to update classroom status.');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await appConfirm({
      title: 'Delete Classroom',
      message: 'Are you sure you want to delete this classroom? This action will archive and remove it from timetable booking.',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (confirmed) {
      try {
        await deleteClassroom(id);
        appToast.success('Classroom deleted successfully.');
        loadClassrooms();
      } catch (e) {
        const msg = e?.response?.data?.detail || 'Failed to delete classroom.';
        appToast.error(typeof msg === 'string' ? msg : 'Failed to delete classroom.');
      }
    }
  };

  const resetFilters = () => {
    setSearchText('');
    setPage(1);
  };

  const actions = {
    setViewMode: (mode) => {
      setViewMode(mode);
      setPage(1);
    },
    setSearchText: (text) => {
      setSearchText(text);
      setPage(1);
    },
    setPage,
    setPageSize,
    resetFilters,
    handleArchive,
    handleDelete,
    refresh: loadClassrooms,
  };

  return {
    state: {
      viewMode,
      searchText,
      page,
      pageSize,
      isLoading,
      error,
      rows: items,
      totalCount: meta.totalCount,
      totalPages: meta.totalPages,
    },
    actions,
  };
}
